import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { TValidationResults } from '@verifime/api-definition';
import {
  CircularProgressButton,
  ConfirmAndCancel,
  StepWrapper,
  TApiScanIdentityDocument,
  TStepContent,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { DocumentType, SERVER_ERRORS } from '@verifime/utils';
import isEqual from 'lodash/isEqual';
import { useState } from 'react';
import DocumentTypeSelectionView from './DocumentTypeSelectionView';
import { FileProvider, useFileUploaderContext } from './FileUploaderContext';
import { FileUploaderView } from './FileUploaderView';
import { FormProvider, useFormContext } from './FormContext';
import FormView from './FormView';
import { buildIdentityDocumentPayload } from './identityDocumentPayload';
import IdScanCompleteView from './IdScanCompleteView';
import ManualEntryTrigger from './ManualEntryTrigger';
import { PhotoGuideProvider } from './PhotoGuideContext';
import PhotoGuideView from './PhotoGuideView';
import { rememberSavedScanId } from './savedScanId';
import { ScanProvider } from './ScanContext';
import ScanIdentityDocumentView from './ScanIdentityDocumentView';
import { ScrollTargetProvider } from './ScrollTargetContext';
import { TApiPutIdentityDocument, TApiPutIdentityDocumentTypeSelections } from './types';
import { useWizardContext } from './WizardContext';

const ERROR_CODE = 500;
const EXCEPTION_TYPE = 'StaleDataException';
const RETRY_DELAY_IN_MS = 1000;
const MAX_RETRY_ATTEMPTS = 10;

const Header = ({ currentStep, allSteps }: { currentStep: number; allSteps: number }) => (
  <Stack gap={tokens.spacingXs} data-component="IdentityDocumentWizardStepHeader">
    {/* TODO: Change to tokens once we have a design token for this */}
    <Typography variant="h5" sx={{ fontSize: '28px' }}>
      Add Verification Documents ({currentStep}/{allSteps})
    </Typography>
    {/* TODO: Change to tokens once we have a design token for this */}
    <Typography variant="body1" sx={{ lineHeight: '150%', letterSpacing: '0.15px' }}>
      Upload valid ID(s) to verify your identity
    </Typography>
  </Stack>
);

type TConfirmIdDocumentConfig = {
  errorCode?: number;
  exceptionType?: string;
  maxRetryAttempts?: number;
  retryDelayInMs?: number;
};

export type TIdentityDocumentWizardStepProps = TStepContent & {
  API: {
    putIdentityDocumentTypeSelections: TApiPutIdentityDocumentTypeSelections;
    putIdentityDocument: TApiPutIdentityDocument;
    scanIdentityDocument: TApiScanIdentityDocument;
  };
  onFinish?: () => void;
  onCancel?: () => void;
  confirmIdDocumentConfig?: TConfirmIdDocumentConfig;
};

const defaultConfirmIdDocumentConfig = {
  errorCode: ERROR_CODE,
  exceptionType: EXCEPTION_TYPE,
  maxRetryAttempts: MAX_RETRY_ATTEMPTS,
  retryDelayInMs: RETRY_DELAY_IN_MS,
};

const IdentityDocumentWizardStepContent = ({
  onBack,
  onNext,
  step,
  stepIndex,
  numSteps,
  API,
  onFinish,
  onCancel,
  confirmIdDocumentConfig = defaultConfirmIdDocumentConfig,
}: TIdentityDocumentWizardStepProps) => {
  const [isShowProgress, setIsShowProgress] = useState(false);

  const {
    state: { customerId, identityDocumentTypeSelections },
    getSelectedDocumentTypeForStep,
    getUploadRequiredForSelectedDocumentTypeForStep,
  } = useWizardContext();
  const {
    state: { fileIds, scanFilesId },
  } = useFileUploaderContext();

  const {
    state: { formData, isValid },
    setValidationResults,
  } = useFormContext();

  const uploadRequired = getUploadRequiredForSelectedDocumentTypeForStep(stepIndex);
  const selectedDocumentType = getSelectedDocumentTypeForStep(stepIndex);

  const handleContinue = async () => {
    const {
      errorCode,
      exceptionType: configuredExceptionType,
      maxRetryAttempts,
      retryDelayInMs,
    } = {
      ...defaultConfirmIdDocumentConfig,
      ...(confirmIdDocumentConfig || {}),
    };

    setIsShowProgress(true);

    let attempt = 0;
    while (attempt <= maxRetryAttempts) {
      try {
        try {
          await API.putIdentityDocumentTypeSelections({
            customerId,
            selections: identityDocumentTypeSelections,
          });
        } catch (error) {
          console.error('Error updating document type selections:', error);
        }

        // Customers only needs to upload OtherIdentityDocument, Admin will do the rest.
        if (selectedDocumentType === DocumentType.OtherIdentityDocument) {
          onFinish?.();
          onNext();
          return;
        }

        const documentData = buildIdentityDocumentPayload(selectedDocumentType, formData);

        await API.putIdentityDocument({
          documentData,
          customerId,
          documentType: selectedDocumentType,
        });
        // The saved value belongs to the scan that is current now (verifime-adr#791).
        rememberSavedScanId(customerId, selectedDocumentType, scanFilesId);
        onFinish?.();
        onNext();
        return;
      } catch (error: any) {
        console.error('Error uploading identity document:', error);
        const { code, originalResponse } = error || {};
        if (code === errorCode) {
          if (originalResponse?.data?.exceptionType?.includes(configuredExceptionType)) {
            if (attempt++ < maxRetryAttempts) {
              await new Promise((resolve) => setTimeout(resolve, retryDelayInMs));
              continue;
            }
          }
        }

        setIsShowProgress(false);

        const validationResults =
          error['validationResults'] ||
          (error.response?.data?.['validationResults'] as TValidationResults);
        if (validationResults?.length > 0) {
          setValidationResults(validationResults);
          return;
        }

        setValidationResults([
          {
            path: '',
            name: '',
            resultType: 'Error',
            resultScope: 'Form',
            message: SERVER_ERRORS.default,
          },
        ]);
        return;
      }
    }
  };

  const shouldDisable =
    selectedDocumentType === DocumentType.OtherIdentityDocument
      ? (fileIds?.length || 0) < 1
      : !formData || isEqual(formData, {}) || !isValid;

  if (!selectedDocumentType) {
    return (
      <CircularProgress
        sx={{
          marginTop: tokens.spacing3xl,
        }}
      />
    );
  }

  return (
    <StepWrapper
      header={<Header currentStep={stepIndex + 1} allSteps={numSteps} />}
      content={
        <Stack gap={tokens.spacingXl} data-component="IdentityDocumentWizardStepContent">
          <DocumentTypeSelectionView currentStep={stepIndex} />
          <FileUploaderView currentStep={stepIndex} />
          {!uploadRequired && selectedDocumentType && (
            <Box data-component="ManualEntryTrigger">
              <ManualEntryTrigger variant="button" resetFormData={false}>
                I‘m unable to upload a copy of my document
              </ManualEntryTrigger>
            </Box>
          )}
          <ScanIdentityDocumentView currentStep={stepIndex} />
          <IdScanCompleteView />
          <PhotoGuideView currentStep={stepIndex} />
          <FormView currentStep={stepIndex} />
        </Stack>
      }
      footer={
        <ConfirmAndCancel
          cancel={
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={onCancel || onBack}
              data-action="back-button"
            >
              Back
            </Button>
          }
          confirm={
            <CircularProgressButton
              fullWidth
              variant="contained"
              size="large"
              disabled={shouldDisable}
              isShowProgress={isShowProgress}
              onClick={handleContinue}
              data-action="continue-button"
            >
              Continue
            </CircularProgressButton>
          }
        />
      }
      step={step}
      numSteps={numSteps}
    />
  );
};

export function IdentityDocumentWizardStep(props: TIdentityDocumentWizardStepProps) {
  return (
    <ScrollTargetProvider>
      <FileProvider>
        <FormProvider>
          <ScanProvider>
            <PhotoGuideProvider>
              <IdentityDocumentWizardStepContent {...props} />
            </PhotoGuideProvider>
          </ScanProvider>
        </FormProvider>
      </FileProvider>
    </ScrollTargetProvider>
  );
}
